import React, { lazy, Suspense, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import qs from 'qs';

import { getName } from 'country-list';
import { Loader, OnMountScroller } from 'ui';
import { CreateNewPortfolioModal } from 'modules/common/Modals';
import { DownloadButton, RoundButton } from 'ui/Buttons';
import { usePortfolioModals, useQuery } from 'shared/hooks';
import { CompanyPanel } from 'ui/Company';
import { DisclaimerModal } from 'ui/Modals';
import { getCompanyData } from 'shared/functions';
import { moduleTypes } from 'shared/constants/moduleTypes';
import { API_URL, equals } from 'utils';
import Paper from '@mui/material/Paper';

import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { ItemType } from 'shared/types/portfolios';
import { useFeatures } from 'shared/hooks';
import { ESGSentimentTab } from 'modules/ESGSentiment';
import { Documents } from 'modules/creditRating';
import { ClimateStressTest } from 'modules/ClimateStressTest';
import { SummaryCardTab } from './SummaryCardTab';
import { Instruments, RatingNews, Research } from './Tabs';

const CreditReviewTab = lazy(() => import('modules/creditReview/CreditReviewTab'));
const ESG = lazy(() => import('modules/ESG/ESG'));
const CreditRatingTab = lazy(() => import('./CreditRatingTab'));

const EntityContent = ({
  easyNumber,
  instruments,
  isRatingRequestPending,
  esgAvailability,
  entityId,
  activeTabs,
  isActiveTab,
  data,
  setActiveTabs,
  currentEnvironmentModules,
  isPending,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { module } = useQuery();
  const { esgSentiment, climateStressTest } = useFeatures();
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const location = useLocation();

  const { createPortfolio, addToPortfolio } = usePortfolioModals();

  const [selectedYear, setSelectedYear] = useState({});
  const isPortfolioAvailable = !isActiveTab(moduleTypes.SUMMARY_CARD);
  const isCreditReviewPDFAvailable = isActiveTab(moduleTypes.CREDIT_REVIEW) && easyNumber;
  const toggleDisclaimerModal = () => setShowDisclaimerModal(!showDisclaimerModal);
  const creditReviewPDFURL = `${API_URL}/sig/generate-report/${easyNumber}`;
  const onChangeTabs = (type, value) => {
    const { companyTab, companyModule } = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    const searchQueryParams = {
      ...(companyTab && { companyTab: activeTabs.companyTab }),
      ...(companyModule && { companyModule: activeTabs.companyModule }),
      module,
      [type]: value,
    };

    const params = {
      ...activeTabs,
      [type]: value,
    };

    navigate({
      search: `?${qs.stringify(searchQueryParams)}`,
    });

    setActiveTabs(params);
  };

  const redirectToEntity = (id) => () =>
    navigate(`/entities/${id}${module ? `?module=${module}` : ''}`);

  const basicData = [
    ...(data.ultimateIssuer && data.ultimateIssuer.name
      ? [
          {
            label: t('Ultimate entity'),
            value: data.ultimateIssuer.name,
            onClick: redirectToEntity(data.ultimateIssuer.id),
          },
        ]
      : []),
    {
      label: t('Industry'),
      value: data.industry,
    },
    {
      label: t('Country'),
      value: getName(data.countryCode),
    },
    {
      label: t('Instruments'),
      value: instruments.total,
    },
  ].filter(({ value }) => value);

  const selectESGYear = (year) => {
    setSelectedYear(year);
  };

  const { availableYears } = esgAvailability || {};

  const { showConflictOfInterests, isRatingSubscription } =
    data && data.products && getCompanyData(data.products);

  const itemsForPortfolio = {
    itemIds: { [entityId]: entityId },
    count: 1,
    type: ItemType.SCOPEONEID,
  };

  const isESGVisible = isActiveTab(moduleTypes.ESG_IMPACT) && esgAvailability !== undefined;

  useEffect(() => {
    if (esgAvailability && availableYears) {
      setSelectedYear(availableYears[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esgAvailability, availableYears]);

  return (
    <>
      <CompanyPanel
        loading={isRatingRequestPending || isPending}
        title={data.name}
        action={
          <>
            {isCreditReviewPDFAvailable && (
              <DownloadButton url={creditReviewPDFURL}>{t('PDF')}</DownloadButton>
            )}
            {isPortfolioAvailable && (
              <RoundButton label={t('Add to portfolio')} onClick={addToPortfolio.show} marginLeft />
            )}
            <RoundButton label={t('View disclaimer')} onClick={toggleDisclaimerModal} marginLeft />
          </>
        }
        items={basicData}
        activeModule={activeTabs.companyModule}
        onChangeModule={onChangeTabs}
        modules={currentEnvironmentModules}
        availableYears={availableYears}
        selectedYear={selectedYear}
        onYearChange={selectESGYear}
      />
      {isActiveTab(moduleTypes.SUMMARY_CARD) && (
        <SummaryCardTab
          onChangeTabs={onChangeTabs}
          spv={data.spv}
          industry={data.industry}
          countryCode={data.countryCode}
          companyName={data.name}
          creditRatingMethodologies={data.methodologies}
          creditRatingDocuments={data.documents}
        />
      )}
      {isActiveTab(moduleTypes.CREDIT_RATING) && (
        <Suspense fallback={<Loader center />}>
          <CreditRatingTab
            companyName={data.name}
            showConflictOfInterests={showConflictOfInterests}
            isRatingSubscription={isRatingSubscription}
            industry={data.industry}
            countryCode={data.countryCode}
            creditRatingMethodologies={data.methodologies}
            creditRatingDocuments={data.documents}
          />
        </Suspense>
      )}
      {isActiveTab(moduleTypes.CREDIT_REVIEW) && (
        <Suspense fallback={<Loader center />}>
          <CreditReviewTab easyNumber={easyNumber} />
        </Suspense>
      )}
      {isESGVisible && (
        <Suspense fallback={<Loader center />}>
          <ESG
            id={entityId}
            entityName={data.name}
            availability={esgAvailability}
            industry={data.industry}
            countryCode={data.countryCode}
            selectedYear={selectedYear}
          />
        </Suspense>
      )}

      {esgSentiment && isActiveTab(moduleTypes.ESG_SENTIMENT) && (
        <Suspense fallback={<Loader center />}>
          <ESGSentimentTab id={entityId} industry={data.industry} countryCode={data.countryCode} />
        </Suspense>
      )}

      {climateStressTest && isActiveTab(moduleTypes.CLIMATE_STRESS_TEST) && (
        <Suspense fallback={<Loader center />}>
          <ClimateStressTest />
        </Suspense>
      )}

      {isActiveTab(moduleTypes.INSTRUMENTS) && (
        <Suspense fallback={<Loader center />}>
          <Paper variant="elevation" elevation={3}>
            <Instruments />
          </Paper>
        </Suspense>
      )}

      {isActiveTab(moduleTypes.RATING_NEWS) && (
        <Suspense fallback={<Loader center />}>
          <Paper variant="elevation" elevation={3}>
            <RatingNews />
          </Paper>
        </Suspense>
      )}

      {isActiveTab(moduleTypes.RESEARCH) && (
        <Suspense fallback={<Loader center />}>
          <Paper variant="elevation" elevation={3}>
            <Research />
          </Paper>
        </Suspense>
      )}

      {isActiveTab(moduleTypes.DOCUMENTS) && (
        <Documents
          reports={data.documents.filter(({ type }) => equals(type, 'Rating Report'))}
          performances={data.documents.filter(({ type }) => equals(type, 'Performance Report'))}
          methodologies={data.methodologies}
        />
      )}

      <DisclaimerModal open={showDisclaimerModal} onClose={toggleDisclaimerModal} />
      {isPortfolioAvailable && (
        <>
          <UpdateEntitiesModal
            open={addToPortfolio.isOpen}
            onClose={addToPortfolio.close}
            onClickCreateNew={createPortfolio.show}
            itemsData={itemsForPortfolio}
            itemType="ENTITY"
          />
          <CreateNewPortfolioModal
            open={createPortfolio.isOpen}
            onClose={createPortfolio.close}
            itemsData={itemsForPortfolio}
            itemType="ENTITY"
          />
        </>
      )}
      <OnMountScroller />
    </>
  );
};

EntityContent.defaultProps = {
  esgAvailability: undefined,
  easyNumber: undefined,
};

EntityContent.propTypes = {
  instruments: PropTypes.object.isRequired,
  isRatingRequestPending: PropTypes.bool.isRequired,
  entityId: PropTypes.string.isRequired,
  activeTabs: PropTypes.shape({
    companyModule: PropTypes.string,
    companyTab: PropTypes.string,
  }).isRequired,
  isActiveTab: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  setActiveTabs: PropTypes.func.isRequired,
  currentEnvironmentModules: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  easyNumber: PropTypes.string,
  esgAvailability: PropTypes.shape({
    data_exists: PropTypes.bool,
    subscribed: PropTypes.bool,
  }),
};

export { EntityContent };
